import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import StoreChips from './components/StoreChips';
import GroceryCard from './components/GroceryCard';
import Filters from './components/Filters';
import ShoppingListDrawer from './components/ShoppingListDrawer';
import SavedRecipesDrawer from './components/SavedRecipesDrawer';
import ThemeToggle from './components/ThemeToggle';
import { GROCERIES_DATA_URL } from './config';
import { initializeTelemetry, trackInteraction, trackFailure } from './services/telemetryService';
import { logger } from './utils/logger';

function App() {
  const [groceriesData, setGroceriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // const [priceRange, setPriceRange] = useState(100);
  const [shoppingList, setShoppingList] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [savedRecipes, setSavedRecipes] = useState({});
  const [isSavedRecipesDrawerOpen, setIsSavedRecipesDrawerOpen] = useState(false);

  const MAX_SAVED_RECIPES = 20;
  const STORAGE_KEY = 'beanTea_savedRecipes';

  // Load saved recipes from localStorage on mount

  // Forcing a PR
  useEffect(() => {
    // Initialize telemetry when app starts
    initializeTelemetry();

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Migration: previous versions stored recipes as an array
        if (Array.isArray(parsed)) {
          const migrated = parsed.reduce((acc, recipe) => {
            const storeKey = recipe.store || 'General';
            if (!acc[storeKey]) {
              acc[storeKey] = [];
            }

            acc[storeKey].push({
              ...recipe,
              price: recipe.price || 0,
              store: storeKey,
            });

            return acc;
          }, {});

          setSavedRecipes(migrated);
        } else if (parsed && typeof parsed === 'object') {
          setSavedRecipes(parsed);
        }
      }
    } catch (error) {
      logger.error('Error loading saved recipes:', error);
    }
  }, []);

  // Save recipes to localStorage whenever savedRecipes changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedRecipes));
    } catch (error) {
      logger.error('Error saving recipes to localStorage:', error);
    }
  }, [savedRecipes]);

  // Fetch groceries data from Azure Blob Storage
  useEffect(() => {
    const fetchGroceriesData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(GROCERIES_DATA_URL);
        logger.log("Data URL:", GROCERIES_DATA_URL);
        if (!response.ok) {
          throw new Error(`Failed to fetch groceries data: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Debug: Check if images exist in the data
        const allItems = data.flatMap(store => store.items || []);
        const itemsWithImages = allItems.filter(item => item.image);
        logger.log(`📦 Loaded ${data.length} stores`);
        logger.log(`🖼️  Items with images: ${itemsWithImages.length} out of ${allItems.length} total items`);
        if (itemsWithImages.length > 0) {
        //  logger.log('✅ Sample item with image:', itemsWithImages[0].name, itemsWithImages[0].image);
        } else {
        //  logger.warn('⚠️  No items have images! Check if data source includes image URLs.');
        }
        
        setGroceriesData(data);
        if (data && data.length > 0) {
          setSelectedStore(data[0].store);
        }
        
      } catch (err) {
        logger.error('Error fetching groceries data:', err);
        trackFailure('Groceries Data Load', err);
        setError(err.message || 'Failed to load groceries data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroceriesData();
  }, []); // Empty dependency array - only fetch on mount

  // Extract unique store names
  const stores = useMemo(() => groceriesData.map(store => store.store), [groceriesData]);

  // Get all items from selected stores
  const allItems = useMemo(() => {
    if (!selectedStore || groceriesData.length === 0) {
      return [];
    }
    
    return groceriesData
      .filter(store => store.store === selectedStore)
      .flatMap(store => 
        store.items.map(item => ({
          ...item,
          store: store.store
        }))
      );
  }, [selectedStore, groceriesData]);

  // Filter items by category and price
  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      const categoryMatch = !selectedCategory || item.category === selectedCategory;
      // const priceMatch = item.price <= priceRange;
      return categoryMatch; // && priceMatch;
    });
  }, [allItems, selectedCategory]); // , priceRange]);

  const handleStoreSelect = (store) => {
    trackInteraction('Select Store', { store });
    setSelectedStore(prevStore => {
      if (prevStore === store) {
        return prevStore;
      }
      setShoppingList([]);
      setSelectedCategory(null);
      return store;
    });
  };

  const handleCategorySelect = (category) => {
    trackInteraction('Select Category', { category });
    if (category === 'All') {
      if (selectedCategory !== null) {
        setSelectedCategory(null);
      }
      return;
    }

    if (selectedCategory !== category) {
      setSelectedCategory(category);
    }
  };

  const handleToggleListItem = (item) => {
    trackInteraction('Toggle List Item', { item_name: item.name });
    setShoppingList(prev => {
      const existingIndex = prev.findIndex(
        listItem => listItem.name === item.name && listItem.store === item.store
      );
      
      if (existingIndex !== -1) {
        // Item is selected - remove it
        return prev.filter((_, i) => i !== existingIndex);
      }
      
      // Item is not selected - add it
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const handleRemoveItem = (index) => {
    setShoppingList(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(index);
      return;
    }
    
    setShoppingList(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const shoppingListCount = shoppingList.reduce((sum, item) => sum + item.quantity, 0);

  const totalSavedRecipes = useMemo(() => {
    return Object.values(savedRecipes).reduce((sum, recipesByStore) => sum + recipesByStore.length, 0);
  }, [savedRecipes]);

  useEffect(() => {
    setShoppingList([]);
  }, [selectedStore]);

  const handleSaveRecipe = (recipe) => {
    trackInteraction('Save Recipe', { store: recipe.store });
    const storeKey = recipe.store || 'General';

    if (totalSavedRecipes >= MAX_SAVED_RECIPES) {
      alert(`You've reached the maximum of ${MAX_SAVED_RECIPES} saved recipes. Please delete some before adding new ones.`);
      return false;
    }

    const storeRecipes = savedRecipes[storeKey] || [];
    const exists = storeRecipes.some(saved => saved.title === recipe.title);
    if (exists) {
      alert('This recipe is already saved for this store!');
      return false;
    }

    const newSavedRecipe = {
      id: `recipe_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      title: recipe.title,
      price: Number(recipe.price) || 0,
      ingredients: recipe.ingredients || [],
      instructions: recipe.instructions || '',
      dateSaved: new Date().toISOString(),
      store: storeKey
    };

    setSavedRecipes(prev => {
      const updatedStoreRecipes = [...(prev[storeKey] || []), newSavedRecipe];
      return {
        ...prev,
        [storeKey]: updatedStoreRecipes
      };
    });

    return true;
  };

  const handleDeleteRecipe = (recipeId, storeKey) => {
    setSavedRecipes(prev => {
      const updatedStoreRecipes = (prev[storeKey] || []).filter(recipe => recipe.id !== recipeId);
      const updatedState = { ...prev };

      if (updatedStoreRecipes.length === 0) {
        delete updatedState[storeKey];
      } else {
        updatedState[storeKey] = updatedStoreRecipes;
      }

      return updatedState;
    });
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <img
              src="/logo.png"
              alt="BeanTea Logo"
              className="app-logo"
              style={{ width: "100px", height: "auto" }}
            />
          {/* <h1 className="app-title">BeanTea Groceries</h1> */}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ThemeToggle />
          {totalSavedRecipes > 0 && (
            <button 
              className="saved-recipes-toggle"
              onClick={() => {
                trackInteraction('Open Saved Recipes Drawer');
                setIsSavedRecipesDrawerOpen(true);
              }}
            >
              📖 Saved Recipes {totalSavedRecipes > 0 && `(${totalSavedRecipes})`}
            </button>
          )}
          <button 
            className="shopping-list-toggle"
            onClick={() => {
              trackInteraction('Open Shopping List Drawer');
              setIsDrawerOpen(true);
            }}
          >
            🛒 Recipe List {shoppingListCount > 0 && `(${shoppingListCount})`}
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="app-content">
          {isLoading ? (
            <div className="loading-message">
              <p>Loading groceries...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <h2>Unable to Load Groceries</h2>
              <p>{error}</p>
              <p className="error-subtitle">Please check your connection and try again later.</p>
            </div>
          ) : (
            <>
              <StoreChips
                stores={stores}
                selectedStore={selectedStore}
                onStoreSelect={handleStoreSelect}
              />

              {selectedStore && (
                <>
              <Filters
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                // priceRange={priceRange}
                // onPriceRangeChange={setPriceRange}
              />

                  {filteredItems.length > 0 ? (
                    <div className="grocery-grid">
                      {filteredItems.map((item, index) => (
                        <GroceryCard
                          key={`${item.store}-${item.name}-${index}`}
                          item={item}
                          onToggleListItem={handleToggleListItem}
                          shoppingList={shoppingList}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="no-items">
                      <p>No items found matching your filters.</p>
                      <p className="no-items-subtitle">Try adjusting your filters or selecting different stores.</p>
                    </div>
                  )}
                </>
              )}

              {(!selectedStore) && stores.length > 0 && (
                <div className="welcome-message">
                  <h2>Welcome to BeanTea Groceries</h2>
                  <p>Select a store above to start browsing!</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <ShoppingListDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        shoppingList={shoppingList}
        onRemoveItem={handleRemoveItem}
        onUpdateQuantity={handleUpdateQuantity}
        activeStore={selectedStore}
        onSaveRecipe={handleSaveRecipe}
      />

      <SavedRecipesDrawer
        isOpen={isSavedRecipesDrawerOpen}
        onClose={() => setIsSavedRecipesDrawerOpen(false)}
        savedRecipes={savedRecipes}
        onDeleteRecipe={handleDeleteRecipe}
      />
    </div>
  );
}

export default App;
